import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import iconRu from '../Translation/icons/lang__ru.png'
import iconBe from '../Translation/icons/lang_be.png'
import iconKz from '../Translation/icons/lang_kz.png'
import iconAr from '../Translation/icons/lang_ar.png'
import iconKg from '../Translation/icons/lang__kg.png'
import iconYz from '../Translation/icons/lang__yz.png'
import iconEn from '../Translation/icons/lang_en.png'
import iconFr from '../Translation/icons/lang_fr.png'
import iconIs from '../Translation/icons/lang_is.png'
import iconIn from '../Translation/icons/lang_in.png'
import { languageMapping } from './constants/languageCodes'
import { setSelectedLanguage } from '@store/languageSlice'
import {
  initGoogleTranslate,
  changeGoogleLanguage,
} from './googleTranslateService'
import s from './index.module.css'

const getCookieLanguage = () => {
  const match = document.cookie.match(/googtrans=\/ru\/(\w+)/)
  return match?.[1] ?? null
}

const CustomGoogleTranslate = () => {
  const dispatch = useDispatch()
  const [currentLang, setCurrentLang] = useState(getCookieLanguage())
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef(null)

  let languages = useSelector((state) => state?.languages) || []
  const activeLanguages = languages
    .filter((el) => Number(el.is_active) === 1)
    .sort((a, b) => a.rank - b.rank)

  useEffect(() => {
    if (!activeLanguages.length) return

    const langs = getLanguagesForComponent()

    const exists = langs.find((l) => l.code === currentLang)
    const defaultLanguage = langs.find((l) => l.code === 'ru') || langs[0]

    if (!exists && !!langs.length) {
      changeLanguage(defaultLanguage.code)
    }
  }, [activeLanguages.length, currentLang])

  useEffect(() => {
    setCurrentLang(getCookieLanguage())
  }, [document.cookie])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getLanguageIcon = (name) => {
    if (name === 'Русский') return iconRu
    if (name === 'Беларусский') return iconBe
    if (name === 'Казахский') return iconKz
    if (name === 'Армянский') return iconAr
    if (name === 'Кыргызский') return iconKg
    if (name === 'Узбекский') return iconYz
    if (name === 'Английский') return iconEn
    if (name === 'Французский') return iconFr
    if (name === 'Испанский') return iconIs
    if (name === 'Индийский') return iconIn

    return null
  }

  const getLanguagesForComponent = () => {
    if (!activeLanguages.length) return []

    return activeLanguages
      .filter((lang) => languageMapping[lang.id])
      .map((lang) => ({
        id: lang.id,
        name: lang.language,
        code: languageMapping[lang.id].code,
        flag: languageMapping[lang.id].flag,
        is_active: lang.is_active,
        rank: lang.rank,
      }))
      .sort((a, b) => a.rank - b.rank)
  }

  // Дополнительный эффект для обработки случая, когда языки загружаются позже
  useEffect(() => {
    if (!activeLanguages.length) return

    const languageCodes = getLanguagesForComponent()
      // .filter((lang) => lang.code !== 'ru')
      .map((lang) => lang.code)

    initGoogleTranslate(languageCodes)
  }, [activeLanguages])

  const changeLanguage = (langCode) => {
    saveLocalStorage(langCode)
    setIsOpen(false)

    const cookieLanguage = getCookieLanguage()

    if (langCode === 'ru' && cookieLanguage && cookieLanguage !== 'ru') {
      // restoreOriginalContent()
      changeGoogleLanguage(langCode)
      window.location.reload()
      return
    }

    changeGoogleLanguage(langCode)

    setCurrentLang(langCode)
  }

  const saveLocalStorage = (langCode) => {
    try {
      const languagesForComponent = getLanguagesForComponent()
      if (languagesForComponent.length === 0) return

      const currentLangObj =
        languagesForComponent.find((lang) => lang.code === langCode) ||
        languagesForComponent[0]
      if (currentLangObj?.id !== undefined) {
        dispatch(setSelectedLanguage(currentLangObj.id))
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  }

  const languagesForComponent = getLanguagesForComponent()

  if (languagesForComponent.length <= 1) return null

  const currentLanguage =
    languagesForComponent.find((lang) => lang.code === currentLang) ||
    languagesForComponent.find((lang) => lang.code === 'ru') ||
    languagesForComponent[0]
  const currentLanguageIcon = getLanguageIcon(currentLanguage.name)

  return (
    <div className={s.languageSelect} ref={wrapperRef}>
      <button
        type="button"
        className={s.languageButton}
        title="Сменить язык"
        aria-label="Сменить язык"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {currentLanguageIcon ? (
          <img
            className={s.languageFlag}
            src={currentLanguageIcon}
            alt={currentLanguage.name}
          />
        ) : (
          <span className={s.languageFallback}>{currentLanguage.flag}</span>
        )}
        <span className={s.languageCode}>{currentLanguage.code}</span>
        <span className={s.languageChevron} />
      </button>

      {isOpen && (
        <div className={s.languageDropdown}>
          {languagesForComponent.map((language) => {
            const languageIcon = getLanguageIcon(language.name)
            const isActive = language.code === currentLanguage.code

            return (
              <button
                type="button"
                key={language.id}
                className={`${s.languageOption} ${isActive ? s.languageOptionActive : ''}`}
                onClick={() => changeLanguage(language.code)}
              >
                {languageIcon ? (
                  <img
                    className={s.languageFlag}
                    src={languageIcon}
                    alt={language.name}
                  />
                ) : (
                  <span className={s.languageFallback}>{language.flag}</span>
                )}
                <span className={s.languageName}>{language.name}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CustomGoogleTranslate
